import React, { useState, useEffect, useMemo } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { useToast } from '../Toast';
import Icon from '../Icon';
import { C, S } from '../../utils/styles';

export default function StateTaxEditor() {
  const { config, updateSingleState } = useConfig();
  const addToast = useToast();
  const [selectedState, setSelectedState] = useState('AL');
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const states = useMemo(() => {
    console.log('🗺️ StateTaxEditor: config.stateData =', config.stateData ? 'exists' : 'MISSING');
    if (config.stateData) {
      console.log('  Number of states:', Object.keys(config.stateData).length);
    }
    return Object.entries(config.stateData)
      .sort((a, b) => a[1].name.localeCompare(b[1].name))
      .filter(([code, s]) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return s.name.toLowerCase().includes(q) || code.toLowerCase().includes(q);
      });
  }, [config.stateData, search]);

  useEffect(() => {
    const stateInfo = config.stateData[selectedState];
    if (stateInfo) {
      setDraft(JSON.parse(JSON.stringify(stateInfo, (k, v) => v === Infinity ? '__INF__' : v)));
      setHasChanges(false);
    }
  }, [selectedState, config.stateData]);

  if (!draft) return null;

  const updateDraftField = (field, value) => {
    setDraft(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const updateBracket = (idx, field, value) => {
    const rates = [...draft.rates];
    if (value === '' || value === 'Infinity') {
      rates[idx] = { ...rates[idx], [field]: '__INF__' };
    } else {
      rates[idx] = { ...rates[idx], [field]: field === 'rate' ? parseFloat(value) || 0 : parseFloat(value) || 0 };
    }
    setDraft(prev => ({ ...prev, rates }));
    setHasChanges(true);
  };

  const addBracket = () => {
    const rates = [...draft.rates];
    const lastMax = rates.length > 0 ? (rates[rates.length - 1].max === '__INF__' ? 999999 : rates[rates.length - 1].max) : 0;
    rates.push({ min: lastMax, max: '__INF__', rate: 0 });
    setDraft(prev => ({ ...prev, rates }));
    setHasChanges(true);
  };

  const removeBracket = (idx) => {
    setDraft(prev => ({ ...prev, rates: prev.rates.filter((_, i) => i !== idx) }));
    setHasChanges(true);
  };

  const save = () => {
    const parsed = JSON.parse(JSON.stringify(draft), (k, v) => v === '__INF__' ? Infinity : v);
    updateSingleState(selectedState, parsed);
    setHasChanges(false);
    addToast(`${draft.name} tax configuration saved`);
  };

  const cancel = () => {
    const stateInfo = config.stateData[selectedState];
    setDraft(JSON.parse(JSON.stringify(stateInfo, (k, v) => v === Infinity ? '__INF__' : v)));
    setHasChanges(false);
  };

  return (
    <div data-testid="state-tax-editor" style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 14, alignItems: 'start' }}>
      {/* State List */}
      <div style={{ ...S.card, padding: '14px', maxHeight: 600, overflowY: 'auto' }}>
        <input
          data-testid="state-search-input"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search states..."
          style={{ ...S.input, fontSize: 11, padding: '6px 10px', marginBottom: 8, fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {states.map(([code, s]) => (
            <button
              key={code}
              onClick={() => setSelectedState(code)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '6px 10px',
                borderRadius: 6,
                border: 'none',
                background: selectedState === code ? C.accentBg : 'transparent',
                color: selectedState === code ? C.accent : C.textMid,
                fontSize: 11,
                fontWeight: selectedState === code ? 700 : 400,
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: "'Inter', sans-serif",
              }}
            >
              <span>{code} — {s.name}</span>
              {s.conformityIncomeTax === false && <span style={{ fontSize: 9, color: C.red, fontWeight: 700 }}>⚠</span>}
            </button>
          ))}
        </div>
      </div>

      {/* State Detail */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h3 style={{ ...S.heading, margin: 0 }}>{selectedState} — {draft.name}</h3>
              {hasChanges && <span style={{ fontSize: 9, color: C.orange, fontWeight: 700, background: C.orangeLight, padding: '2px 8px', borderRadius: 4 }}>Unsaved Changes</span>}
              {!draft.conformityIncomeTax && <span style={{ fontSize: 9, color: C.red, fontWeight: 700, background: C.redLight, padding: '2px 8px', borderRadius: 4 }}>Non-Conforming</span>}
            </div>
          </div>

          {/* Payroll Taxes */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
            <div style={{ padding: '10px', background: C.bg, borderRadius: 8 }}>
              <label style={{ ...S.label, marginBottom: 4 }}>SDI (%)</label>
              <input
                type="number"
                value={((draft.sdi || 0) * 100).toFixed(3)}
                onChange={(e) => updateDraftField('sdi', parseFloat(e.target.value) / 100 || 0)}
                step="0.001"
                style={{ ...S.input, fontSize: 11, padding: '4px 8px' }}
              />
            </div>
            <div style={{ padding: '10px', background: C.bg, borderRadius: 8 }}>
              <label style={{ ...S.label, marginBottom: 4 }}>PFML (%)</label>
              <input
                type="number"
                value={((draft.pfml || 0) * 100).toFixed(3)}
                onChange={(e) => updateDraftField('pfml', parseFloat(e.target.value) / 100 || 0)}
                step="0.001"
                style={{ ...S.input, fontSize: 11, padding: '4px 8px' }}
              />
            </div>
            <div style={{ padding: '10px', background: C.bg, borderRadius: 8 }}>
              <label style={{ ...S.label, marginBottom: 4 }}>FLI (%)</label>
              <input
                type="number"
                value={((draft.fli || 0) * 100).toFixed(3)}
                onChange={(e) => updateDraftField('fli', parseFloat(e.target.value) / 100 || 0)}
                step="0.001"
                style={{ ...S.input, fontSize: 11, padding: '4px 8px' }}
              />
            </div>
            <div style={{ padding: '10px', background: C.bg, borderRadius: 8 }}>
              <label style={{ ...S.label, marginBottom: 4 }}>IRC §125 Conformity</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <button
                  data-testid="conformity-toggle"
                  onClick={() => updateDraftField('conformityIncomeTax', !draft.conformityIncomeTax)}
                  style={{
                    width: 40, height: 22, borderRadius: 11, border: 'none',
                    background: draft.conformityIncomeTax ? C.green : C.red,
                    position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: '50%', background: '#fff',
                    position: 'absolute', top: 3,
                    left: draft.conformityIncomeTax ? 21 : 3,
                    transition: 'left 0.2s',
                  }} />
                </button>
                <span style={{ fontSize: 11, color: draft.conformityIncomeTax ? C.green : C.red, fontWeight: 700 }}>
                  {draft.conformityIncomeTax ? 'Conforms' : 'Non-Conforming'}
                </span>
              </div>
            </div>
          </div>

          {/* Note */}
          <div style={{ marginBottom: 14 }}>
            <label style={S.label}>Compliance Note</label>
            <input
              type="text"
              value={draft.note || ''}
              onChange={(e) => updateDraftField('note', e.target.value)}
              placeholder="Optional compliance note"
              style={{ ...S.input, fontSize: 11, padding: '6px 10px', fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
            />
          </div>

          {/* Income Tax Brackets */}
          <h3 style={S.heading}>Income Tax Brackets</h3>
          {draft.rates.length === 0 ? (
            <div style={{ padding: '16px', background: C.bg, borderRadius: 8, textAlign: 'center', fontSize: 12, color: C.textLight }}>
              No state income tax
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={S.th}>Min ($)</th>
                  <th style={S.th}>Max ($)</th>
                  <th style={S.th}>Rate (%)</th>
                  <th style={{ ...S.th, width: 60, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {draft.rates.map((b, idx) => (
                  <tr key={idx} style={{ background: idx % 2 === 0 ? 'transparent' : C.bg }}>
                    <td style={S.td}>
                      <input
                        type="number"
                        value={b.min ?? ''}
                        onChange={(e) => updateBracket(idx, 'min', e.target.value)}
                        style={{ ...S.input, fontSize: 11, padding: '4px 8px', width: 110 }}
                      />
                    </td>
                    <td style={S.td}>
                      {b.max === '__INF__' ? (
                        <span style={{ ...S.mono, fontSize: 14, color: C.textLight }}>∞</span>
                      ) : (
                        <input
                          type="number"
                          value={b.max ?? ''}
                          onChange={(e) => updateBracket(idx, 'max', e.target.value || 'Infinity')}
                          style={{ ...S.input, fontSize: 11, padding: '4px 8px', width: 110 }}
                        />
                      )}
                    </td>
                    <td style={S.td}>
                      <input
                        type="number"
                        value={((b.rate || 0) * 100).toFixed(2)}
                        onChange={(e) => updateBracket(idx, 'rate', parseFloat(e.target.value) / 100)}
                        step="0.01"
                        style={{ ...S.input, fontSize: 11, padding: '4px 8px', width: 80 }}
                      />
                    </td>
                    <td style={{ ...S.td, textAlign: 'center' }}>
                      <button onClick={() => removeBracket(idx)} style={{ padding: '4px 8px', background: C.redLight, color: C.red, borderRadius: 4, border: 'none', fontSize: 10, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Icon name="trash-outline" style={{ fontSize: '14px' }} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'space-between' }}>
            <button 
              onClick={addBracket} 
              style={{
                ...S.buttonSecondary,
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 4
              }}
            >
              + Add Bracket
            </button>
            {hasChanges && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button 
                  onClick={cancel} 
                  style={S.buttonSecondary}
                >
                  Cancel
                </button>
                <button 
                  data-testid="save-state-btn" 
                  onClick={save} 
                  style={S.buttonPrimary}
                >
                  Save Changes
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
